import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { useTheme } from '@/components/theme-provider';
import { ActivityIndicator } from '@/components/ui/activity-indicator';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { useBrowseLevel } from '@/hooks/use-document-browse';
import { usePortfolioSearch } from '@/hooks/use-document-search';
import { formatDocumentSize } from '@/utils/document-download';

/**
 * Pick files from Dropbox for an email (#606).
 *
 * Inline in the composer, not a modal over it: the composer is itself a floating window, and a
 * second layer is where the modal → persistent-panel traps live. Opens in the association's
 * folder (the user's call) with a breadcrumb back up, plus portfolio-wide search.
 *
 * What it can show is exactly what the Documents library can (the user's call: Option A) — it
 * reads through the same browse and search endpoints, and the server re-checks every picked id
 * with `assertFileVisible` before copying a byte.
 */

export interface PickedDropboxFile {
	id: string;
	name: string;
	size: number | null;
}

interface DropboxPickerProps {
	/** Root-relative, `/`-joined folder to open in, or null for the library root. */
	startPath: string | null;
	onAttach: (files: PickedDropboxFile[]) => void;
	onCancel: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;

export const DropboxPicker: React.FC<DropboxPickerProps> = ({ startPath, onAttach, onCancel }) => {
	const theme = useTheme();
	const muted = theme.colors.onSurfaceVariant;
	const [path, setPath] = useState(startPath ?? '');
	const [query, setQuery] = useState('');
	const [debounced, setDebounced] = useState('');
	const [picked, setPicked] = useState<ReadonlyMap<string, PickedDropboxFile>>(new Map());

	// Debounced, and the input stays mounted throughout (CLAUDE.md "Stable Search Inputs").
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [query]);

	/**
	 * The MODE follows the live input; only the request waits for the debounce. Keyed on
	 * `debounced`, the folder's rows stayed up — and tickable — for 300ms under a search box that
	 * already said something else, and came back late after the box was cleared.
	 */
	const searching = query.trim().length > 0;
	const browse = useBrowseLevel(path, !searching);
	const search = usePortfolioSearch(searching ? debounced : '');
	/**
	 * Only results FOR the query on screen. The search hook keeps the previous page mounted while
	 * the next loads; in a picker those stale hits are tickable, so a file from the last search
	 * could be attached while the box already shows a different one. The same holds for the
	 * debounce window: `debounced` still names the OLD query for 300ms after an edit, so the
	 * live input must match it too.
	 */
	const results = searching && query.trim() === debounced && search.data?.query === debounced
		? search.data.items
		: null;

	const segments = useMemo(() => (path ? path.split('/') : []), [path]);
	const toggle = (file: PickedDropboxFile) => setPicked((current) => {
		const next = new Map(current);
		if (next.has(file.id)) next.delete(file.id); else next.set(file.id, file);
		return next;
	});
	const pickedBytes = [...picked.values()].reduce((sum, f) => sum + (f.size ?? 0), 0);

	const fileRow = (file: { id: string; name: string; size?: number | null }, detail?: string) => {
		const checked = picked.has(file.id);
		return (
			<Pressable
				key={file.id}
				onPress={() => toggle({ id: file.id, name: file.name, size: file.size ?? null })}
				// `role`/`aria-*`: RN-Web 0.21 drops `accessibilityState`.
				role="checkbox"
				aria-checked={checked}
				testID={`dropbox-file-${file.id}`}
				style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }}
			>
				<Ionicons name={checked ? 'checkbox' : 'square-outline'} size={16} color={checked ? theme.colors.primary : muted} />
				<Text style={{ flex: 1, minWidth: 0 }} numberOfLines={1}>{file.name}</Text>
				<Text style={{ fontSize: 11, color: muted }} numberOfLines={1}>
					{[detail, file.size != null ? formatDocumentSize(file.size) : null].filter(Boolean).join(' · ')}
				</Text>
			</Pressable>
		);
	};

	return (
		<View
			testID="dropbox-picker"
			style={{
				marginTop: 8, padding: 10, borderRadius: 8, borderWidth: 1,
				borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
			}}
		>
			<Text style={{ fontSize: 12, fontWeight: '600', marginBottom: 6 }}>Attach from Dropbox</Text>
			<TextInput
				value={query}
				onChangeText={setQuery}
				placeholder="Search all documents…"
				autoCapitalize="none"
				testID="dropbox-search"
			/>

			{searching ? (
				// A ScrollView, not a View: `maxHeight` on a View clips — search returns up to 200 rows.
				<ScrollView testID="dropbox-results" style={{ marginTop: 6, maxHeight: 280 }}>
					{/*
						A failed search is NOT a slow one. The hook does not retry, so without this
						branch a failure sat on "Searching…" forever with no way to try again.
					*/}
					{!results && search.isError && query.trim() === debounced ? (
						<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6 }}>
							<Text testID="dropbox-search-error" style={{ flex: 1, fontSize: 12, color: theme.colors.error }}>
								Search failed{search.error instanceof Error && search.error.message ? `: ${search.error.message}` : '.'}
							</Text>
							<Button mode="text" compact onPress={() => { void search.refetch(); }} testID="btn-dropbox-search-retry">
								Try again
							</Button>
						</View>
					) : !results ? (
						<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6 }}>
							<ActivityIndicator size="small" />
							<Text style={{ fontSize: 12, color: muted }}>Searching…</Text>
						</View>
					) : results.length ? (
						results.map((item) => fileRow(item, item.association ?? undefined))
					) : (
						<Text style={{ fontSize: 12, color: muted, paddingVertical: 6 }}>No files match “{debounced}”.</Text>
					)}
				</ScrollView>
			) : (
				<View testID="dropbox-browse" style={{ marginTop: 6 }}>
					{/* Breadcrumb: the library root, then each folder down to here. */}
					<View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginBottom: 4 }}>
						<Pressable onPress={() => setPath('')} testID="dropbox-crumb-root">
							<Text style={{ fontSize: 12, color: theme.colors.primary }}>Documents</Text>
						</Pressable>
						{segments.map((segment, index) => (
							<View key={`${index}-${segment}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
								<Text style={{ fontSize: 12, color: muted }}>/</Text>
								<Pressable
									onPress={() => setPath(segments.slice(0, index + 1).join('/'))}
									testID={`dropbox-crumb-${index}`}
								>
									<Text style={{ fontSize: 12, color: index === segments.length - 1 ? theme.colors.onSurface : theme.colors.primary }}>
										{segment}
									</Text>
								</Pressable>
							</View>
						))}
					</View>
					{browse.isLoading ? (
						<ActivityIndicator size="small" />
					) : browse.error ? (
						<Text style={{ fontSize: 12, color: theme.colors.error }}>Could not open this folder.</Text>
					) : browse.data?.unavailable ? (
						<Text style={{ fontSize: 12, color: muted }}>Dropbox is not connected.</Text>
					) : (
						<ScrollView testID="dropbox-browse-list" style={{ maxHeight: 280 }}>
							{(browse.data?.folders ?? []).map((folder) => (
								<Pressable
									key={folder.id || folder.name}
									onPress={() => setPath(path ? `${path}/${folder.name}` : folder.name)}
									testID={`dropbox-folder-${folder.name}`}
									style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }}
								>
									<Ionicons name="folder-outline" size={16} color={muted} />
									<Text style={{ flex: 1, minWidth: 0 }} numberOfLines={1}>{folder.name}</Text>
								</Pressable>
							))}
							{(browse.data?.files ?? []).map((file) => fileRow(file))}
							{!browse.data?.folders?.length && !browse.data?.files?.length ? (
								<Text style={{ fontSize: 12, color: muted, paddingVertical: 6 }}>This folder is empty.</Text>
							) : null}
						</ScrollView>
					)}
				</View>
			)}

			<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
				{picked.size ? (
					<Text style={{ flex: 1, fontSize: 11, color: muted }}>
						{picked.size} selected{pickedBytes ? ` · ${formatDocumentSize(pickedBytes)}` : ''}
					</Text>
				) : null}
				<Button mode="outlined" compact onPress={onCancel} testID="btn-dropbox-cancel">Cancel</Button>
				<Button
					mode="contained"
					compact
					onPress={() => onAttach([...picked.values()])}
					disabled={!picked.size}
					testID="btn-dropbox-attach"
				>
					{picked.size ? `Attach ${picked.size}` : 'Attach'}
				</Button>
			</View>
		</View>
	);
};
